import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styles from './CheckInSidePanel.module.scss';
import cx from 'classnames';
import { Mood } from '@peekapak/types/Mood';
import { StudentWithCheckIns } from './TeacherCheckInTool';
import { ClassroomType } from '@peekapak/types/DataProtocolTypes';
import { getPrimaryEmotion } from '../CheckIn/checkInValues';
import type { MiddleSchoolCheckInEntry } from '@peekapak/types/ReportDataExchangeTypes';
import happyEmoji from '../images/teacher-check-in/emoticon-happy.png';
import excitedEmoji from '../images/teacher-check-in/emoticon-excited.png';
import sadEmoji from '../images/teacher-check-in/emoticon-sad.png';
import angryEmoji from '../images/teacher-check-in/emoticon-angry.png';
import calmEmoji from '../images/teacher-check-in/emoticon-calm.png';
import nervousEmoji from '../images/teacher-check-in/emoticon-nervous.png';

interface CheckInSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  students: StudentWithCheckIns[];
  classroom: ClassroomType | null;
}

const CheckInSidePanel: React.FC<CheckInSidePanelProps> = ({ isOpen, onClose, onOpen, students, classroom }) => {
  const isSecondary = classroom && classroom.grade > 5;

  // Elementary mood configuration with emojis (6 moods)
  const ELEMENTARY_MOOD_CONFIG = [
    { mood: Mood.HAPPY, emojiSrc: happyEmoji, label: 'Happy', color: '#fcd34d' },
    { mood: Mood.EXCITED, emojiSrc: excitedEmoji, label: 'Excited', color: '#f9a8d4' },
    { mood: Mood.SAD, emojiSrc: sadEmoji, label: 'Sad', color: '#93c5fd' },
    { mood: Mood.ANGRY, emojiSrc: angryEmoji, label: 'Angry', color: '#fca5a5' },
    { mood: Mood.CALM, emojiSrc: calmEmoji, label: 'Calm', color: '#86efac' },
    { mood: Mood.NERVOUS, emojiSrc: nervousEmoji, label: 'Nervous', color: '#c4b5fd' },
  ];

  // Secondary mood configuration (7 primary emotions)
  const SECONDARY_MOOD_CONFIG = [
    { mood: 'Calm', emojiSrc: calmEmoji, label: 'Calm', color: '#bfe5e4' },
    { mood: 'Happy', emojiSrc: happyEmoji, label: 'Happy', color: '#fdf0aa' },
    { mood: 'Confident', label: 'Confident', color: '#f58955' },
    { mood: 'Surprised', label: 'Surprised', color: '#f15e4a' },
    { mood: 'Angry', emojiSrc: angryEmoji, label: 'Angry', color: '#181597' },
    { mood: 'Scared', emojiSrc: nervousEmoji, label: 'Scared', color: '#5982bc' },
    { mood: 'Sad', emojiSrc: sadEmoji, label: 'Sad', color: '#6db8e3' },
  ];

  const MOOD_CONFIG = isSecondary ? SECONDARY_MOOD_CONFIG : ELEMENTARY_MOOD_CONFIG;

  // Calculate mood tallies from today's check-ins
  const moodTallies = useMemo(() => {
    const tallies: Record<string, number> = {};

    // Initialize all moods with 0
    MOOD_CONFIG.forEach(({ mood }) => {
      tallies[mood] = 0;
    });

    if (isSecondary) {
      // Secondary (grades 6-12): Count string-based emotions
      students.forEach((student) => {
        student.todaysCheckIns.forEach((checkIn) => {
          // For secondary, check if the checkIn has a string 'feeling' property
          const middleSchoolCheckIn = checkIn as unknown as MiddleSchoolCheckInEntry;
          if (typeof middleSchoolCheckIn.feeling === 'string') {
            const primaryEmotion = getPrimaryEmotion(middleSchoolCheckIn.feeling);
            if (tallies[primaryEmotion] !== undefined) {
              tallies[primaryEmotion]++;
            }
          }
        });
      });
    } else {
      // Elementary (grades K-5): Count numeric-based moods
      students.forEach((student) => {
        student.todaysCheckIns.forEach((checkIn) => {
          // Map numeric feeling values to Mood enum (based on moodToNumberMap)
          const moodMap: Record<number, string> = {
            1: Mood.HAPPY,
            2: Mood.EXCITED,
            3: Mood.SAD,
            4: Mood.ANGRY,
            5: Mood.CALM,
            6: Mood.NERVOUS,
          };

          if (typeof checkIn.feeling === 'number') {
            const moodKey = moodMap[checkIn.feeling];
            if (moodKey && tallies[moodKey] !== undefined) {
              tallies[moodKey]++;
            }
          }
        });
      });
    }

    return tallies;
  }, [students, isSecondary, MOOD_CONFIG]);
  return (
    <>
      {/* Backdrop */}
      <div
        className={cx(styles.backdrop, {
          [styles.visible]: isOpen,
        })}
        onClick={onClose}
      />

      {/* Side Panel */}
      <div
        className={cx(styles.sidePanel, {
          [styles.open]: isOpen,
        })}
      >
        {/* Panel Header */}
        <div className={styles.panelHeader}>
          <h2>Check-In Summary</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label='Close panel'>
            <svg fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>

        {/* Panel Content */}
        <div className={styles.panelContent}>
          <div className={styles.moodTallySection}>
            <h3>Today's Check-In Summary</h3>
            <div className={styles.moodGrid}>
              {MOOD_CONFIG.map(({ mood, emojiSrc, label, color }) => (
                <div key={mood} className={styles.moodCard} style={{ borderColor: color }}>
                  {emojiSrc ? (
                    <div className={styles.moodEmoji}>
                      <img src={emojiSrc} alt={label} />
                    </div>
                  ) : (
                    <div className={styles.moodEmojiText} style={{ backgroundColor: color }}>
                      {label.charAt(0)}
                    </div>
                  )}
                  <div className={styles.moodLabel}>{label}</div>
                  <div className={styles.moodCount}>
                    <span className={styles.count}>{moodTallies[mood]}</span>
                    {/* <span className={styles.countLabel}>{moodTallies[mood] === 1 ? 'student' : 'students'}</span> */}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.totalSection}>
              <div className={styles.totalCard}>
                <span className={styles.totalLabel}>Total Check-Ins Today</span>
                <span className={styles.totalCount}>
                  {Object.values(moodTallies).reduce((sum, count) => sum + count, 0)}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h2>Help & Resources</h2>
            <div className={styles.resourceLinks}>
              {classroom && (
                <Link
                  to={classroom.grade > 5 ? '/reports/MoodboardSecondary' : '/reports/Moodboard'}
                  className={styles.resourceLink}
                >
                  <svg
                    className={styles.linkIcon}
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                    width='16'
                    height='16'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
                    />
                  </svg>
                  View Student Moodboard Report
                </Link>
              )}
              <p className={styles.resourceDescription}>
                Track your students' emotional journey over time with detailed mood analytics and insights.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab to open panel */}
      <button
        className={cx(styles.activationTab, {
          [styles.hidden]: isOpen,
        })}
        onClick={onOpen}
        aria-label='Open summary panel'
      >
        <div className={styles.tabContent}>
          <svg fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
            />
          </svg>
          <span>Summary</span>
        </div>
      </button>
    </>
  );
};

export default CheckInSidePanel;
